import type { Summary } from '|/app/services';
import currency, { removeZeroCents } from '|/helpers/currency';
import type {
  GetPaymentSummaryResponse,
  PaymentSelection
} from '../services/purchase-payments-api.types';

export type PriceIsRightFundingType = 'finance' | 'saf' | 'cash';

export interface PriceIsRightVehicleView {
  imageUrl: string | null;
  title: string;
  trim: string | null;
}

export interface PriceIsRightBreakdownItem {
  id: 'cash-down' | 'subtotal' | 'shipping' | 'previous-payment';
  amount: number;
  formattedAmount: string;
  label: string;
  tone: 'default' | 'success';
  expandable: boolean;
}

export type PriceIsRightPaymentMethodType =
  | 'bank-account'
  | 'card'
  | 'apple-pay'
  | 'google-pay'
  | 'digital-wallet'
  | 'cashiers-check'
  | 'wire-transfer';

export interface PriceIsRightPaymentMethodView {
  type: PriceIsRightPaymentMethodType;
  label: string;
  mask: string | null;
  cardBrand: string | null;
  purpose: 'payment' | 'refund';
}

export interface PriceIsRightView {
  fundingType: PriceIsRightFundingType;
  vehicle: PriceIsRightVehicleView;
  headline: {
    label: 'Total due now' | 'Refund';
    amount: number;
    formattedAmount: string;
  };
  breakdownItems: PriceIsRightBreakdownItem[];
  paymentMethod: PriceIsRightPaymentMethodView | null;
}

const normalize = (value?: string | null) => value?.trim().toLowerCase() ?? '';

const formatPriceIsRightCurrency = (amount: number) => removeZeroCents(currency(amount));

export const formatPriceIsRightHeadlineCurrency = formatPriceIsRightCurrency;

const deriveFundingType = (
  paymentSummary: GetPaymentSummaryResponse,
  reviewSummary: Summary | null
): PriceIsRightFundingType => {
  const fundingType = normalize(reviewSummary?.funding?.type);
  const institution = normalize(reviewSummary?.funding?.institution);

  if (fundingType === 'cash') return 'cash';
  if (fundingType === 'thirdparty') return 'saf';
  if (fundingType === 'financing') return institution === 'carvana' ? 'finance' : 'saf';

  const paymentType = normalize(paymentSummary.paymentType);
  if (paymentType === 'cash') return 'cash';
  if (paymentType.includes('self') || paymentType.includes('third') || paymentType === 'saf') {
    return 'saf';
  }

  return 'finance';
};

const paymentSelectionToView = (
  paymentSelection: PaymentSelection | undefined,
  purpose: PriceIsRightPaymentMethodView['purpose']
): PriceIsRightPaymentMethodView | null => {
  switch (paymentSelection?.selectionType) {
    case 'BankAccount':
      return paymentSelection.bankAccount
        ? {
            type: 'bank-account',
            label: 'Account',
            mask: paymentSelection.bankAccount.accountMask,
            cardBrand: null,
            purpose
          }
        : null;

    case 'Card':
      return paymentSelection.card
        ? {
            type: 'card',
            label: 'Card',
            mask: paymentSelection.card.cardMask,
            cardBrand: paymentSelection.card.cardBrand,
            purpose
          }
        : null;

    case 'DigitalWallet':
      if (purpose === 'refund') {
        return paymentSelection.card
          ? {
              type: 'card',
              label: 'Card',
              mask: paymentSelection.card.cardMask,
              cardBrand: paymentSelection.card.cardBrand,
              purpose
            }
          : null;
      }

      if (paymentSelection.digitalWallet === 'ApplePay') {
        return {
          type: 'apple-pay',
          label: 'Apple Pay',
          mask: null,
          cardBrand: null,
          purpose
        };
      }

      if (paymentSelection.digitalWallet === 'GooglePay') {
        return {
          type: 'google-pay',
          label: 'Google Pay',
          mask: null,
          cardBrand: null,
          purpose
        };
      }

      return {
        type: 'digital-wallet',
        label: 'Apple Pay or Google Pay',
        mask: null,
        cardBrand: null,
        purpose
      };

    case 'CashiersCheck':
      return {
        type: 'cashiers-check',
        label: "Cashier's check",
        mask: null,
        cardBrand: null,
        purpose
      };

    case 'WireTransfer':
      return {
        type: 'wire-transfer',
        label: 'Wire transfer',
        mask: null,
        cardBrand: null,
        purpose
      };

    default:
      return null;
  }
};

const reviewSummaryPaymentMethodToView = (
  reviewSummary: Summary | null,
  purpose: PriceIsRightPaymentMethodView['purpose'],
  requireCard = false
): PriceIsRightPaymentMethodView | null => {
  const paymentAccounts = reviewSummary?.payment?.paymentAccounts ?? [];
  const downPaymentAccount = paymentAccounts.find(
    paymentAccount => paymentAccount.type === 'DownPayment'
  );
  const isCardBackedAccount = (paymentAccount: (typeof paymentAccounts)[number] | undefined) =>
    paymentAccount?.selectionType === 'Card' ||
    (paymentAccount?.selectionType === 'DigitalWallet' && !!paymentAccount.mask);
  const account = requireCard
    ? (isCardBackedAccount(downPaymentAccount) ? downPaymentAccount : undefined) ??
      paymentAccounts.find(isCardBackedAccount)
    : purpose === 'refund'
      ? (downPaymentAccount?.selectionType === 'Card' ? downPaymentAccount : undefined) ??
        paymentAccounts.find(paymentAccount => paymentAccount.selectionType === 'Card') ??
        downPaymentAccount ??
        paymentAccounts[0]
      : downPaymentAccount ?? paymentAccounts[0];

  switch (account?.selectionType) {
    case 'BankAccount':
      return {
        type: 'bank-account',
        label: 'Account',
        mask: account.mask ?? null,
        cardBrand: null,
        purpose
      };

    case 'Card':
      return {
        type: 'card',
        label: 'Card',
        mask: account.mask ?? null,
        cardBrand: account.cardBrand ?? null,
        purpose
      };

    case 'DigitalWallet':
      if (purpose === 'refund') {
        return account.mask
          ? {
              type: 'card',
              label: 'Card',
              mask: account.mask,
              cardBrand: account.cardBrand ?? null,
              purpose
            }
          : null;
      }

      if (account.digitalWallet === 'ApplePay') {
        return {
          type: 'apple-pay',
          label: 'Apple Pay',
          mask: null,
          cardBrand: null,
          purpose
        };
      }

      if (account.digitalWallet === 'GooglePay') {
        return {
          type: 'google-pay',
          label: 'Google Pay',
          mask: null,
          cardBrand: null,
          purpose
        };
      }

      return {
        type: 'digital-wallet',
        label: 'Apple Pay or Google Pay',
        mask: null,
        cardBrand: null,
        purpose
      };

    case 'CashiersCheck':
      return {
        type: 'cashiers-check',
        label: "Cashier's check",
        mask: null,
        cardBrand: null,
        purpose
      };

    case 'WireTransfer':
      return {
        type: 'wire-transfer',
        label: 'Wire transfer',
        mask: null,
        cardBrand: null,
        purpose
      };

    default:
      return null;
  }
};

const deriveBreakdownItems = (
  fundingType: PriceIsRightFundingType,
  paymentSummary: GetPaymentSummaryResponse
): PriceIsRightBreakdownItem[] => {
  const { cashDown, shippingFee, credit } = paymentSummary.purchasePaymentBreakdown;

  const items: PriceIsRightBreakdownItem[] = [];

  if (cashDown > 0) {
    const isCash = fundingType === 'cash';
    items.push({
      id: isCash ? 'subtotal' : 'cash-down',
      amount: cashDown,
      formattedAmount: formatPriceIsRightCurrency(cashDown),
      label: isCash ? 'subtotal' : 'cash down payment',
      tone: 'default',
      expandable: false
    });
  }

  if (shippingFee > 0) {
    items.push({
      id: 'shipping',
      amount: shippingFee,
      formattedAmount: formatPriceIsRightCurrency(shippingFee),
      label: 'nonrefundable shipping',
      tone: 'default',
      expandable: true
    });
  }

  if (credit > 0) {
    items.push({
      id: 'previous-payment',
      amount: credit,
      formattedAmount: formatPriceIsRightCurrency(credit),
      label: 'previous payment applied',
      tone: 'success',
      expandable: false
    });
  }

  // A single contributing amount is already explained by the headline. Figma
  // introduces the "Includes" group only when two or more amounts form the total.
  return items.length > 1 ? items : [];
};

export const derivePriceIsRightView = (
  paymentSummary: GetPaymentSummaryResponse,
  reviewSummary: Summary | null
): PriceIsRightView => {
  const fundingType = deriveFundingType(paymentSummary, reviewSummary);
  const { amountDue, refundDue } = paymentSummary.purchasePaymentBreakdown;
  const isRefund = refundDue > 0;
  const purpose: PriceIsRightPaymentMethodView['purpose'] = isRefund ? 'refund' : 'payment';
  const shouldShowPaymentMethod = amountDue > 0 || isRefund;
  const isDigitalWalletRefund =
    isRefund && paymentSummary.paymentSelection?.selectionType === 'DigitalWallet';
  const vehicle = reviewSummary?.vehicle;
  const vehicleTitle = [vehicle?.year, vehicle?.make, vehicle?.model].filter(Boolean).join(' ');
  const vehicleTrim = vehicle?.trim?.trim();

  return {
    fundingType,
    vehicle: {
      imageUrl: vehicle?.primaryImageUrl ?? vehicle?.fallbackImageUrl ?? null,
      title: vehicleTitle ? vehicleTitle : 'Your vehicle',
      trim: vehicleTrim ? vehicleTrim : null
    },
    headline: {
      label: isRefund ? 'Refund' : 'Total due now',
      amount: isRefund ? refundDue : amountDue,
      formattedAmount: formatPriceIsRightHeadlineCurrency(isRefund ? refundDue : amountDue)
    },
    breakdownItems: deriveBreakdownItems(fundingType, paymentSummary),
    paymentMethod: shouldShowPaymentMethod
      ? paymentSelectionToView(paymentSummary.paymentSelection, purpose) ??
        reviewSummaryPaymentMethodToView(reviewSummary, purpose, isDigitalWalletRefund)
      : null
  };
};

export default derivePriceIsRightView;
